import {
	createContext,
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';

import { Box, IconButton } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';

import { ChevronLeftIcon, ChevronRightIcon } from '@/components/Icons';

const CarouselContext = createContext(null);

const toSxArray = sx => (Array.isArray(sx) ? sx : [sx]);

function useCarousel() {
	const context = useContext(CarouselContext);

	if (!context) {
		throw new Error('useCarousel must be used within a <Carousel />');
	}

	return context;
}

const Carousel = forwardRef(
	(
		{
			orientation = 'horizontal',
			opts,
			setApi,
			plugins,
			sx = [],
			children,
			onKeyDownCapture,
			...props
		},
		ref
	) => {
		const [carouselRef, api] = useEmblaCarousel(
			{
				...opts,
				axis: orientation === 'horizontal' ? 'x' : 'y'
			},
			plugins
		);

		const [canScrollPrev, setCanScrollPrev] = useState(false);
		const [canScrollNext, setCanScrollNext] = useState(false);

		const onSelect = useCallback(apiParam => {
			setCanScrollPrev(apiParam.canScrollPrev());
			setCanScrollNext(apiParam.canScrollNext());
		}, []);

		const scrollPrev = useCallback(() => {
			api?.scrollPrev();
		}, [api]);

		const scrollNext = useCallback(() => {
			api?.scrollNext();
		}, [api]);

		const handleKeyDown = useCallback(
			event => {
				onKeyDownCapture?.(event);

				if (event.defaultPrevented) return;

				if (
					event.target instanceof HTMLElement &&
					event.target.closest(
						'input, textarea, select, [contenteditable="true"]'
					)
				) {
					return;
				}

				const prevKey =
					orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
				const nextKey =
					orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';

				if (event.key === prevKey) {
					event.preventDefault();
					scrollPrev();
				} else if (event.key === nextKey) {
					event.preventDefault();
					scrollNext();
				}
			},
			[orientation, scrollPrev, scrollNext, onKeyDownCapture]
		);

		useEffect(() => {
			if (api && setApi) {
				setApi(api);
			}
		}, [api, setApi]);

		useEffect(() => {
			if (!api) return;

			onSelect(api);

			api.on('reInit', onSelect);
			api.on('select', onSelect);

			return () => {
				api.off('reInit', onSelect);
				api.off('select', onSelect);
			};
		}, [api, onSelect]);

		const contextValue = useMemo(
			() => ({
				carouselRef,
				api,
				opts,
				orientation,
				scrollPrev,
				scrollNext,
				canScrollPrev,
				canScrollNext
			}),
			[
				carouselRef,
				api,
				opts,
				orientation,
				scrollPrev,
				scrollNext,
				canScrollPrev,
				canScrollNext
			]
		);

		return (
			<CarouselContext.Provider value={contextValue}>
				<Box
					ref={ref}
					role="region"
					aria-roledescription="carousel"
					tabIndex={0}
					{...props}
					onKeyDownCapture={handleKeyDown}
					sx={[{ position: 'relative' }, ...toSxArray(sx)]}
				>
					{children}
				</Box>
			</CarouselContext.Provider>
		);
	}
);
Carousel.displayName = 'Carousel';

const CarouselContent = forwardRef(
	({ sx = [], viewportProps = {}, ...props }, ref) => {
		const { carouselRef, orientation } = useCarousel();
		const horizontal = orientation === 'horizontal';
		const { sx: viewportSx = [], ...restViewportProps } = viewportProps;

		return (
			<Box
				ref={carouselRef}
				{...restViewportProps}
				sx={[{ overflow: 'hidden' }, ...toSxArray(viewportSx)]}
			>
				<Box
					ref={ref}
					{...props}
					sx={[
						{
							display: 'flex',
							flexDirection: horizontal ? 'row' : 'column',
							...(horizontal ? { ml: '-16px' } : { mt: '-16px' }),
							touchAction: horizontal
								? 'pan-y pinch-zoom'
								: 'pan-x pinch-zoom'
						},
						...toSxArray(sx)
					]}
				/>
			</Box>
		);
	}
);
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = forwardRef(({ sx = [], ...props }, ref) => {
	const { orientation } = useCarousel();

	return (
		<Box
			ref={ref}
			role="group"
			aria-roledescription="slide"
			{...props}
			sx={[
				{
					minWidth: 0,
					flex: '0 0 100%',
					boxSizing: 'border-box',
					...(orientation === 'horizontal'
						? { pl: '16px' }
						: { pt: '16px' })
				},
				...toSxArray(sx)
			]}
		/>
	);
});
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = forwardRef(
	({ sx = [], disabled, onClick, ...props }, ref) => {
		const { orientation, scrollPrev, canScrollPrev } = useCarousel();
		const horizontal = orientation === 'horizontal';

		return (
			<IconButton
				ref={ref}
				aria-label="Previous slide"
				{...props}
				disabled={!canScrollPrev || disabled}
				onClick={event => {
					onClick?.(event);
					if (!event.defaultPrevented) scrollPrev();
				}}
				sx={[
					{
						position: 'absolute',
						width: 32,
						height: 32,
						border: '1px solid',
						borderColor: 'divider',
						...(horizontal
							? {
									top: '50%',
									left: '-48px',
									transform: 'translateY(-50%)'
								}
							: {
									top: '-48px',
									left: '50%',
									transform: 'translateX(-50%) rotate(90deg)'
								})
					},
					...toSxArray(sx)
				]}
			>
				<ChevronLeftIcon
					aria-hidden="true"
					style={{ width: 16, height: 16 }}
				/>
			</IconButton>
		);
	}
);
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = forwardRef(
	({ sx = [], disabled, onClick, ...props }, ref) => {
		const { orientation, scrollNext, canScrollNext } = useCarousel();
		const horizontal = orientation === 'horizontal';

		return (
			<IconButton
				ref={ref}
				aria-label="Next slide"
				{...props}
				disabled={!canScrollNext || disabled}
				onClick={event => {
					onClick?.(event);
					if (!event.defaultPrevented) scrollNext();
				}}
				sx={[
					{
						position: 'absolute',
						width: 32,
						height: 32,
						border: '1px solid',
						borderColor: 'divider',
						...(horizontal
							? {
									top: '50%',
									right: '-48px',
									transform: 'translateY(-50%)'
								}
							: {
									bottom: '-48px',
									left: '50%',
									transform: 'translateX(-50%) rotate(90deg)'
								})
					},
					...toSxArray(sx)
				]}
			>
				<ChevronRightIcon
					aria-hidden="true"
					style={{ width: 16, height: 16 }}
				/>
			</IconButton>
		);
	}
);
CarouselNext.displayName = 'CarouselNext';

export {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselPrevious,
	CarouselNext
};
